/**
 * HoneyCub Shop by Age — growth ruler controller.
 *
 * Band count is read from the DOM, never hardcoded, so adding or removing
 * age band blocks in the Theme Editor works with no code change.
 *
 * Handles: pointer drag, click on the ruler, label buttons, and full
 * keyboard support (arrows / home / end) via role="slider".
 * All visual movement is CSS transition — this only sets position and state.
 */

const SECTION_SELECTOR = '[data-hc-age]';

class HoneyCubShopByAge {
  constructor(root) {
    this.root = root;
    this.ruler = root.querySelector('[data-ruler]');
    this.marker = root.querySelector('[data-marker]');
    this.ticks = root.querySelector('[data-ticks]');
    this.labels = Array.from(root.querySelectorAll('[data-label]'));
    this.panels = Array.from(root.querySelectorAll('[data-panel]'));

    this.count = this.labels.length;
    if (this.count === 0) return;

    this.current = 0;
    this.dragging = false;

    this.buildTicks();

    this.labels.forEach((btn) => {
      btn.addEventListener('click', () => this.select(Number(btn.dataset.i)));
    });

    if (this.ruler) {
      this.ruler.addEventListener('pointerdown', this.onDown);
      this.ruler.addEventListener('pointermove', this.onMove);
      this.ruler.addEventListener('pointerup', this.onUp);
      this.ruler.addEventListener('pointercancel', this.onUp);
      this.ruler.addEventListener('keydown', this.onKey);
    }

    this.select(0, true);
  }

  buildTicks() {
    if (!this.ticks) return;
    // Roughly 10 ticks per band, so density stays consistent at any count.
    const total = this.count * 10;
    let html = '';
    for (let i = 0; i <= total; i++) {
      const major = i % 10 === 0;
      const mid = i % 5 === 0;
      const h = major ? 18 : mid ? 12 : 7;
      html += `<span class="${major ? 'is-major' : ''}" style="left:${(i / total) * 100}%;height:${h}px"></span>`;
    }
    this.ticks.innerHTML = html;
  }

  positionFor(index) {
    return ((index + 0.5) / this.count) * 100;
  }

  indexFromX(clientX) {
    const rect = this.ruler.getBoundingClientRect();
    if (!rect.width) return this.current;
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(this.count - 1, Math.floor(ratio * this.count)));
  }

  select(index, force = false) {
    if (!force && index === this.current) return;
    this.current = index;

    if (this.marker) this.marker.style.left = `${this.positionFor(index)}%`;

    this.labels.forEach((btn, i) => {
      const on = i === index;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    this.panels.forEach((panel, i) => {
      if (i === index) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    if (this.ruler) {
      this.ruler.setAttribute('aria-valuenow', String(index + 1));
      const label = this.labels[index]?.querySelector('.hc-age__label-age')?.textContent?.trim();
      if (label) this.ruler.setAttribute('aria-valuetext', label);
    }
  }

  onDown = (e) => {
    this.dragging = true;
    this.ruler.setPointerCapture(e.pointerId);
    this.marker?.classList.add('is-dragging');
    this.select(this.indexFromX(e.clientX));
  };

  onMove = (e) => {
    if (this.dragging) this.select(this.indexFromX(e.clientX));
  };

  onUp = () => {
    if (!this.dragging) return;
    this.dragging = false;
    this.marker?.classList.remove('is-dragging');
  };

  onKey = (e) => {
    const keys = {
      ArrowRight: 1,
      ArrowUp: 1,
      ArrowLeft: -1,
      ArrowDown: -1,
    };

    if (e.key in keys) {
      e.preventDefault();
      this.select(Math.max(0, Math.min(this.count - 1, this.current + keys[e.key])));
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.select(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.select(this.count - 1);
    }
  };
}

const instances = new WeakMap();

function init(scope = document) {
  const roots =
    scope.matches && scope.matches(SECTION_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(SECTION_SELECTOR));

  roots.forEach((root) => {
    if (instances.has(root)) return;
    instances.set(root, new HoneyCubShopByAge(root));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (e) => {
  if (e.target instanceof HTMLElement) init(e.target);
});
document.addEventListener('shopify:section:unload', (e) => {
  if (e.target instanceof HTMLElement) instances.delete(e.target);
});